import ShopRegiTree from "./ShopRegiTree";

/**
 * 一覧テーブルの列定義（MonthlyDataPage と同じ構成）
 */
const COLUMNS = [
  { key: "GenkinUriage",  label: "現金売上" },
  { key: "KakeUriage",    label: "掛売上" },
  { key: "SonotaUriage",  label: "その他売上" },
  { key: "SyohiZei",      label: "うち消費税" },
  { key: "SonotaKoujo",   label: "その他控除" },
  { key: "SoUriage",      label: "総売上" },
  { key: "JunUriage",     label: "純売上" },
  { key: "CustNo",        label: "客数" },
  { key: "GenkinAridaka", label: "入金機投入額" },
  { key: "_kabusoku",     label: "現金相違額", signed: true,
    getValue: (row) => row ? toNum(row.GenkinUriage) - toNum(row.GenkinAridaka) : null },
  { key: "InputPerson",   label: "担当者", isText: true },
];

// null/undefined/NaN は 0 として数値変換する
const toNum = (v) => (v == null ? 0 : Number(v));

// null/undefined は空文字、数値はカンマ区切りで表示
const formatNumber = (v) => (v == null ? "" : Number(v).toLocaleString());

// 符号付きフォーマット: 正の値に "+" を付与する
const formatSigned = (v) => (v == null ? "" : (v > 0 ? "+" : "") + Number(v).toLocaleString());

// YYYYMMDD → YYYY/MM/DD に変換して表示用にする
const formatDateDisplay = (yyyymmdd) =>
  `${yyyymmdd.slice(0, 4)}/${yyyymmdd.slice(4, 6)}/${yyyymmdd.slice(6, 8)}`;

// 指定年月の全日付を YYYYMMDD 配列で返す
const getDaysInMonth = (yearMonth) => {
  const year = parseInt(yearMonth.slice(0, 4), 10);
  const month = parseInt(yearMonth.slice(4, 6), 10);
  const lastDay = new Date(year, month, 0).getDate();
  return Array.from({ length: lastDay }, (_, i) => {
    const d = String(i + 1).padStart(2, "0");
    return `${year}${String(month).padStart(2, "0")}${d}`;
  });
};

/**
 * 特定レジの月次データテーブル
 */
function RegiDataTable({ shopNo, regiNo, reports, yearMonth }) {
  const days = getDaysInMonth(yearMonth);
  const regiReports = reports.filter(
    (r) => String(r.ShopNo) === String(shopNo) && String(r.RegiNo) === String(regiNo)
  );

  return (
    <div className="overflow-x-auto ml-12 mt-1 mb-2">
      <table className="border-collapse text-sm">
        <thead>
          <tr>
            <th className="border border-gray-300 px-3 py-1 font-normal bg-gray-50 whitespace-nowrap text-left">
              日付
            </th>
            {COLUMNS.map((col) => (
              <th
                key={col.key}
                className="border border-gray-300 px-3 py-1 font-normal bg-gray-50 whitespace-nowrap"
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {days.map((yyyymmdd) => {
            const row = regiReports.find((r) => r.YYYYMMDD === yyyymmdd) ?? null;
            return (
              <tr key={yyyymmdd} className="hover:bg-gray-50">
                <td className="border border-gray-300 px-3 py-1 whitespace-nowrap">
                  {formatDateDisplay(yyyymmdd)}
                </td>
                {COLUMNS.map((col) => {
                  let displayValue;
                  let colorClass = "";
                  if (col.isText) {
                    displayValue = row ? (row.UpdatePerson ?? row.InputPerson ?? "") : "";
                  } else if (col.getValue) {
                    const v = col.getValue(row);
                    displayValue = col.signed ? formatSigned(v) : formatNumber(v);
                    // 現金相違額: 非ゼロのとき赤色表示（StoreInputForm・MonthlyDataPageと同じパターン）
                    if (col.key === "_kabusoku" && v) colorClass = "text-red-600";
                  } else {
                    displayValue = formatNumber(row?.[col.key]);
                  }
                  return (
                    <td
                      key={col.key}
                      className={`border border-gray-300 px-3 py-1 ${col.isText ? "text-left" : "text-right"} ${colorClass}`}
                    >
                      {displayValue}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
        {/* 合計行 */}
        {regiReports.length > 0 && (
          <tfoot>
            <tr className="bg-gray-100 font-medium">
              <td className="border border-gray-300 px-3 py-1 whitespace-nowrap">合計</td>
              {COLUMNS.map((col) => {
                if (col.isText) {
                  return <td key={col.key} className="border border-gray-300 px-3 py-1" />;
                }
                const total = regiReports.reduce((sum, row) => {
                  const v = col.getValue ? col.getValue(row) : toNum(row[col.key]);
                  return sum + toNum(v);
                }, 0);
                // 現金相違額: 非ゼロのとき赤色表示（StoreInputForm・MonthlyDataPageと同じパターン）
                const colorClass = col.key === "_kabusoku" && total ? "text-red-600" : "";
                return (
                  <td key={col.key} className={`border border-gray-300 px-3 py-1 text-right ${colorClass}`}>
                    {col.signed ? formatSigned(total) : formatNumber(total)}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

/**
 * AllShopsTreeView - 全店舗データ管理画面用ツリービュー
 *
 * 店舗はデフォルト全展開（レジ一覧が常に見える状態）。
 * レジのデータ部分はデフォルト折りたたみ（クリックで開く。不要なデータを毎回閉じる手間を避けるため）。
 * グループ化・展開状態の管理は ShopRegiTree（CommentTreeView と共通基盤）に委ねている。
 *
 * @param {object[]} regiInfo  - 全レジ情報 [{ShopNo, ShopName, RegiNo, RegiName}]
 * @param {object[]} reports   - 全店舗月次データ [{YYYYMMDD, ShopNo, RegiNo, ...}]
 * @param {string}   yearMonth - 表示中の年月 (YYYYMM形式)
 */
function AllShopsTreeView({ regiInfo, reports, yearMonth }) {
  const shops = regiInfo.filter((r) => r.ShopNo !== 0);

  return (
    <ShopRegiTree
      regiInfo={regiInfo}
      defaultExpandedShops={new Set(shops.map((r) => r.ShopNo))}
      defaultExpandedRegis={new Set()}
      renderRegiContent={(shopNo, regiNo) => (
        <RegiDataTable shopNo={shopNo} regiNo={regiNo} reports={reports} yearMonth={yearMonth} />
      )}
    />
  );
}

export default AllShopsTreeView;
